import React, { useState, useMemo, useCallback } from "react";
import { RefreshCw } from "lucide-react";
import { useDashboardData } from "./hooks/useDashboardData";
import { applyEmployeeFilters } from "./services/dashboardFilters";
import { DashboardHeader } from "./components/DashboardHeader";
import { ActiveFilterChips } from "./components/ActiveFIlterChips";

import { EmployeeGrowthCard } from "./components/cards/EmployeeGrowthCard";
import { EmployeeLifecycleCard } from "./components/cards/EmployeeLifecycleCard";
import { DepartmentDistributionCard } from "./components/cards/DepartmentDistributionCard";
import { LeaveOverviewCard } from "./components/cards/LeaveOverviewCard";
import { OnboardingCard } from "./components/cards/OnboardingCard";
import { AssetsOverviewCard } from "./components/cards/AssetsOverviewCard";

function HRDashboard() {
  const { employees, assets, leaves, onboarding, loading, refreshing, error, lastUpdated, refresh } =
    useDashboardData();

  const [filters, setFilters] = useState({
    selectedDepartment: "ALL",
    selectedEmployeeStatus: "ALL",
    selectedGrowthRange: "6_MONTHS",
    assetView: "STATUS",
  });

  const toggleDepartment = useCallback((dept) => {
    setFilters((f) => ({ ...f, selectedDepartment: f.selectedDepartment === dept ? "ALL" : dept }));
  }, []);

  const toggleStatus = useCallback((status) => {
    setFilters((f) => ({ ...f, selectedEmployeeStatus: f.selectedEmployeeStatus === status ? "ALL" : status }));
  }, []);

  const setGrowthRange = useCallback((v) => setFilters((f) => ({ ...f, selectedGrowthRange: v })), []);
  const setAssetView = useCallback((v) => setFilters((f) => ({ ...f, assetView: v })), []);

  const clearOne = useCallback((key) => {
    setFilters((f) => ({ ...f, ...(key === "dept" ? { selectedDepartment: "ALL" } : { selectedEmployeeStatus: "ALL" }) }));
  }, []);

  const clearAll = useCallback(
    () => setFilters((f) => ({ ...f, selectedDepartment: "ALL", selectedEmployeeStatus: "ALL" })),
    []
  );

  const filteredEmployees = useMemo(
    () => applyEmployeeFilters(employees, filters),
    [employees, filters]
  );

  const departmentScopedEmployees = useMemo(
    () => employees.filter((e) => filters.selectedEmployeeStatus === "ALL" || e.status === filters.selectedEmployeeStatus),
    [employees, filters.selectedEmployeeStatus]
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="flex flex-col items-center gap-3 text-slate-400">
          <RefreshCw className="animate-spin" size={26} />
          <span className="text-sm font-medium">Loading dashboard data…</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex font-sans">
      <main className="flex-1 min-w-0">
        <DashboardHeader onRefresh={refresh} refreshing={refreshing} lastUpdated={lastUpdated} />

        {error && (
          <div className="mx-6 lg:mx-8 mb-4 bg-red-50 text-red-700 text-sm font-medium rounded-xl px-4 py-3 flex items-center justify-between">
            {error}
            <button onClick={refresh} className="font-semibold underline">
              Retry
            </button>
          </div>
        )}

        <ActiveFilterChips filters={filters} onClear={clearOne} onClearAll={clearAll} />

        <div className="px-6 lg:px-8 pb-10 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          <EmployeeGrowthCard
            employees={filteredEmployees}
            range={filters.selectedGrowthRange}
            onRangeChange={setGrowthRange}
          />
          <EmployeeLifecycleCard
            employees={filteredEmployees}
            activeStatus={filters.selectedEmployeeStatus}
            onStatusClick={toggleStatus}
          />
          <DepartmentDistributionCard
            employees={departmentScopedEmployees}
            activeDept={filters.selectedDepartment}
            onDeptClick={toggleDepartment}
          />

          <LeaveOverviewCard leaves={leaves} />
          <OnboardingCard onboarding={onboarding} />
          <AssetsOverviewCard
            assets={assets}
            assetView={filters.assetView}
            onAssetViewChange={setAssetView}
          />
        </div>
      </main>
    </div>
  );
}

export default HRDashboard;